import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Col,
  Form,
  FormGroup,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import InputMask from "react-input-mask";
import { GetLanguageString } from "../../helper/Components";
import { applyClassToCharacters, LoadingSpinner } from "../../../constants/const";
import Draggable from "react-draggable";
import { GetSelectedLanguage } from "../../helper/Method";
import { useTranslation } from "react-i18next";
import { setupApi } from "../../member/ApiMember";
import { ApiFinance } from "../ApiFinance";
import Select from "react-select";
import swal from "sweetalert";

const sectionHeader = (color, border) => ({
  fontWeight: 600,
  color,
  borderBottom: `2px solid ${border}`,
  paddingBottom: "6px",
  marginBottom: "16px",
  fontSize: "14px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

// Renders single language based on active language or propKey
const SectionTitle = ({ en, ur, propKey }) => {
  const lang = GetSelectedLanguage();
  if (propKey) return <GetLanguageString props={propKey} />;
  return <span>{lang === "ur" ? applyClassToCharacters(ur, "ur") : en}</span>;
};

const existingCardStyle = (selected) => ({
  border: `1.5px solid ${selected ? "#2563EB" : "#E5E7EB"}`,
  borderRadius: "10px",
  padding: "12px 16px",
  marginBottom: "10px",
  background: selected ? "#EFF6FF" : "#FAFAFA",
  boxShadow: selected ? "0 0 0 2px #BFDBFE" : "0 1px 3px rgba(0,0,0,0.05)",
  transition: "all 0.2s ease",
  cursor: "pointer",
});

const pillStyle = (color, bg) => ({
  fontSize: "11px",
  fontWeight: 600,
  color,
  background: bg,
  borderRadius: "20px",
  padding: "2px 8px",
  display: "inline-block",
});

const emptyValues = {
  id: 0,
  bankId: "",
  accountTitle: "",
  accountNumber: "",
  accountIban: "",
  accountBranch: "",
  vendorName: "",
  cnic: "",
  ntnNumber: "",
  vendorType: "individual",
  isFiler: false,
  filerUseCnic: false,
};

const AddVendorBankAccountModal = ({ setModal, getAddedBankAccount, onDeleteBankAccount }) => {
  const { t } = useTranslation();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [banksList, setBanksList] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [existingAccounts, setExistingAccounts] = useState([]);
  const [cnicSearchLoading, setCnicSearchLoading] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const searchTimeoutRef = useRef(null);

  const handleDrag = (e, ui) => {
    const { x, y } = position;
    setPosition({ x: x + ui.deltaX, y: y + ui.deltaY });
  };

  useEffect(() => {
    setLoading(true);
    setupApi()
      .GetAllBanks()
      .then((res) => {
        setBanksList(res.data);
        if (res.data.length > 0) {
          setSelectedBank(res.data[0]);
          setValues((prev) => ({ ...prev, bankId: res.data[0].value }));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // 1-second debounced search effect for CNIC vs NTN
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (values.vendorType === "company") {
      const ntn = (values.ntnNumber || "").trim();
      if (ntn.length >= 3) {
        setCnicSearchLoading(true);
        searchTimeoutRef.current = setTimeout(() => {
          ApiFinance()
            .GetVendorBankAccountsByCnic(ntn, "ntn")
            .then((res) => setExistingAccounts(res.data || []))
            .catch(() => setExistingAccounts([]))
            .finally(() => setCnicSearchLoading(false));
        }, 1000);
      } else {
        setExistingAccounts([]);
        setSelectedCardId(null);
        setIsEditMode(false);
      }
    } else {
      const cnic = values.cnic || "";
      if (cnic.length === 13) {
        setCnicSearchLoading(true);
        searchTimeoutRef.current = setTimeout(() => {
          ApiFinance()
            .GetVendorBankAccountsByCnic(cnic, "cnic")
            .then((res) => setExistingAccounts(res.data || []))
            .catch(() => setExistingAccounts([]))
            .finally(() => setCnicSearchLoading(false));
        }, 1000);
      } else {
        setExistingAccounts([]);
        setSelectedCardId(null);
        setIsEditMode(false);
      }
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [values.cnic, values.ntnNumber, values.vendorType]);

  const handleInputChangeCnic = (e) => {
    const stripped = e.target.value.replace(/-/g, "");
    setValues((prev) => ({ ...prev, cnic: stripped }));
    setErrors((prev) => ({ ...prev, cnic: "" }));
  };

  const handleInputChangeNtn = (e) => {
    const val = e.target.value;
    setValues((prev) => ({ ...prev, ntnNumber: val }));
    setErrors((prev) => ({ ...prev, ntnNumber: "" }));
  };

  const handleEditCard = (acc) => {
    setSelectedCardId(acc.id);
    setIsEditMode(true);
    setErrors({});
    const matchedBank = banksList.find((b) => b.value === acc.bankId) || null;
    setSelectedBank(matchedBank);
    const isCompany = !!acc.ntnNumber;
    const isFiler = !!acc.isFiler;
    const filerUseCnic = isFiler && !acc.ntnNumber && !!acc.cnic;
    setValues({
      id: acc.id,
      bankId: acc.bankId,
      accountTitle: acc.accountTitle,
      accountNumber: acc.accountNumber,
      accountIban: acc.accountIban || "",
      accountBranch: acc.accountBranch || "",
      vendorName: acc.vendorName || "",
      cnic: acc.cnic || "",
      ntnNumber: acc.ntnNumber || "",
      vendorType: isCompany ? "company" : "individual",
      isFiler: isFiler,
      filerUseCnic: filerUseCnic,
    });
  };

  const handleDeleteCard = (acc) => {
    swal({
      title: "Are you sure?",
      text: `Delete bank account: ${acc.accountTitle}?`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) {
        ApiFinance()
          .DeleteVendorBankAccount(acc.id)
          .then(() => {
            setExistingAccounts((prev) => prev.filter((a) => a.id !== acc.id));
            if (selectedCardId === acc.id) {
              setSelectedCardId(null);
              setIsEditMode(false);
              setValues((prev) => ({ ...emptyValues, cnic: prev.cnic, bankId: banksList[0]?.value || "" }));
              setSelectedBank(banksList[0] || null);
            }
            // Notify parent so its dropdown list refreshes
            if (onDeleteBankAccount) onDeleteBankAccount(acc.id);
          })
          .catch(() => swal({ title: "Error", text: "Could not delete.", icon: "error" }));
      }
    });
  };

  const handleAddNew = () => {
    setSelectedCardId(null);
    setIsEditMode(false);
    setErrors({});
    setValues((prev) => ({ ...emptyValues, cnic: prev.cnic, bankId: banksList[0]?.value || "" }));
    setSelectedBank(banksList[0] || null);
  };

  const handleChangeText = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleBankChange = (selectedOption) => {
    setSelectedBank(selectedOption);
    setValues((prev) => ({ ...prev, bankId: selectedOption.value }));
    setErrors((prev) => ({ ...prev, selectedBank: "" }));
  };

  const validate = () => {
    const temp = {};
    let isValid = true;
    if (!values.bankId) { temp.selectedBank = t("common_field_required_error"); isValid = false; }
    if (!values.accountTitle?.trim()) { temp.accountTitle = t("common_field_required_error"); isValid = false; }
    if (!values.accountNumber?.trim()) { temp.accountNumber = t("common_field_required_error"); isValid = false; }
    if (!values.vendorName?.trim()) { temp.vendorName = t("common_field_required_error"); isValid = false; }

    if (values.isFiler) {
      if (values.filerUseCnic) {
        if (!values.cnic?.trim()) {
          temp.cnic = t("member_cnic_error_message") || "CNIC is required";
          isValid = false;
        } else if (values.cnic.length !== 13) {
          temp.cnic = "CNIC is not valid. It must be 13 numbers.";
          isValid = false;
        }
      } else {
        if (!values.ntnNumber?.trim()) {
          temp.ntnNumber = t("common_field_required_error") || "NTN Number is required";
          isValid = false;
        }
      }
    } else {
      if (!values.cnic?.trim()) {
        temp.cnic = t("member_cnic_error_message") || "CNIC is required";
        isValid = false;
      } else if (values.cnic.length !== 13) {
        temp.cnic = "CNIC is not valid. It must be 13 numbers.";
        isValid = false;
      }
    }
    setErrors(temp);
    return isValid;
  };

  const handleSubmit = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!validate()) return;
    setLoading(true);
    const apiCall = isEditMode
      ? ApiFinance().UpdateVendorBankAccount(values)
      : ApiFinance().AddVendorBankAccount(values);
    apiCall
      .then((res) => {
        if (res.data.statusCode === "200") {
          swal({
            title: isEditMode ? "Updated!" : (t("transactions_bank_account_added") || "Bank Account Added"),
            className: GetSelectedLanguage() === "en" ? "" : "ur",
            icon: "success",
            buttons: "OK",
          });
          getAddedBankAccount(res.data.addedAccount);
          setModal(false);
        }
      })
      .catch((error) => {
        swal({ title: "Error", text: error.response?.data || "Operation failed", icon: "error", buttons: "OK" });
      })
      .finally(() => setLoading(false));
  };

  const renderExistingAccounts = () => {
    if (cnicSearchLoading) {
      return (
        <Col md="12">
          <div className="text-center" style={{ padding: "10px 0" }}>
            <LoadingSpinner />
          </div>
        </Col>
      );
    }
    if (existingAccounts.length === 0) return null;
    return (
      <Col md="12">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
          <h6 style={{ margin: 0, fontWeight: 600, color: "#4B5563", fontSize: "13px" }}>
            <i className="fa fa-list" style={{ marginRight: "6px", color: "#6366F1" }}></i>
            Existing Accounts ({existingAccounts.length})
          </h6>
          {isEditMode && (
            <Button size="sm" color="success" outline className="no-drag" onClick={handleAddNew} style={{ fontSize: "11px", padding: "2px 10px" }}>
              <i className="fa fa-plus" style={{ marginRight: "4px" }}></i> Add New
            </Button>
          )}
        </div>
        {existingAccounts.map((acc) => (
          <div key={acc.id} style={existingCardStyle(selectedCardId === acc.id)} onClick={() => handleEditCard(acc)}>
            <Row>
              <Col md="8">
                <div style={{ fontWeight: 600, fontSize: "13px", color: "#111827", marginBottom: "4px" }}>
                  {applyClassToCharacters(acc.accountTitle, "ur")}
                  {selectedCardId === acc.id && (
                    <span style={{ ...pillStyle("#1D4ED8", "#DBEAFE"), marginLeft: "8px" }}>Editing</span>
                  )}
                </div>
                <div style={{ fontSize: "12px", color: "#6B7280", display: "flex", gap: "14px", flexWrap: "wrap" }}>
                  <span><i className="fa fa-bank" style={{ marginRight: "4px", color: "#9CA3AF" }}></i>{acc.bankName}</span>
                  <span><i className="fa fa-hashtag" style={{ marginRight: "4px", color: "#9CA3AF" }}></i>{acc.accountNumber}</span>
                  {acc.accountIban && <span><i className="fa fa-credit-card" style={{ marginRight: "4px", color: "#9CA3AF" }}></i>{acc.accountIban}</span>}
                </div>
              </Col>
              <Col md="4" style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "6px" }}>
                <Button size="sm" color="primary" outline className="no-drag" onClick={(e) => { e.stopPropagation(); handleEditCard(acc); }} style={{ fontSize: "11px", padding: "3px 10px" }}>
                  <i className="fa fa-pencil" style={{ marginRight: "3px" }}></i> Edit
                </Button>
                <Button size="sm" color="danger" outline className="no-drag" onClick={(e) => { e.stopPropagation(); handleDeleteCard(acc); }} style={{ fontSize: "11px", padding: "3px 10px" }}>
                  <i className="fa fa-trash" style={{ marginRight: "3px" }}></i> Delete
                </Button>
              </Col>
            </Row>
          </div>
        ))}
        <hr style={{ borderColor: "#E5E7EB", margin: "12px 0" }} />
      </Col>
    );
  };

  return (
    <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
      <Modal isOpen className="inmodal" size="lg" autoFocus={false}>
        <ModalHeader className="modal-title" tag="h4">
          <GetLanguageString props="transactions_vendor_bank_details" />
        </ModalHeader>
        {loading ? (
          <ModalBody>
            <div className="text-center" style={{ width: "100%" }}><LoadingSpinner /></div>
          </ModalBody>
        ) : (
          <Form onSubmit={handleSubmit}>
            <ModalBody style={{ padding: "20px 24px" }}>
              <Row>
                <Col md="12">
                  <h5 style={sectionHeader("#2563EB", "#3B82F6")}>
                    <i className="fa fa-user"></i>
                    <SectionTitle en="VENDOR DETAILS" ur="وینڈر کی تفصیلات" />
                  </h5>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label" style={{ marginBottom: "6px" }}>
                      <GetLanguageString props="transactions_vendor_name" />
                    </Label>
                    <Input name="vendorName" type="text" className="no-drag" value={values.vendorName || ""} onChange={handleChangeText} invalid={!!errors.vendorName} />
                    {errors.vendorName && <h4 className="text-error no-drag">{errors.vendorName}</h4>}
                  </FormGroup>
                </Col>

                {/* Top Right: Individual / Company Radio Header + CNIC/NTN Input Field */}
                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label" style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "6px", minHeight: "24px" }}>
                      <Label check style={{ cursor: "pointer", fontSize: "13px", margin: 0, fontWeight: "normal" }}>
                        <Input
                          type="radio"
                          name="vendorType"
                          value="individual"
                          checked={values.vendorType !== "company"}
                          onChange={() => {
                            setValues((prev) => ({ ...prev, vendorType: "individual" }));
                          }}
                        />{" "}
                        <GetLanguageString props="vendor_type_individual" />
                      </Label>
                      <Label check style={{ cursor: "pointer", fontSize: "13px", margin: 0, fontWeight: "normal" }}>
                        <Input
                          type="radio"
                          name="vendorType"
                          value="company"
                          checked={values.vendorType === "company"}
                          onChange={() => {
                            setValues((prev) => ({ ...prev, vendorType: "company" }));
                          }}
                        />{" "}
                        <GetLanguageString props="vendor_type_company" />
                      </Label>
                    </Label>

                    {values.isFiler && !values.filerUseCnic ? (
                      <>
                        <Input
                          name="ntnNumber"
                          type="text"
                          className="no-drag"
                          placeholder="Enter NTN Number"
                          value={values.ntnNumber || ""}
                          onChange={handleInputChangeNtn}
                          invalid={!!errors.ntnNumber}
                        />
                        {errors.ntnNumber && <h4 className="text-error no-drag">{errors.ntnNumber}</h4>}
                      </>
                    ) : (
                      <>
                        <InputMask mask="99999-9999999-9" value={values.cnic || ""} onChange={handleInputChangeCnic} name="cnic" maskChar="">
                          {(inputProps) => (
                            <Input {...inputProps} type="text" className="no-drag" placeholder="______-_______-_" invalid={!!errors.cnic} />
                          )}
                        </InputMask>
                        {errors.cnic && <FormFeedback><h4>{errors.cnic}</h4></FormFeedback>}
                      </>
                    )}

                    {/* Option / Indicator at bottom of field (black & italic) */}
                    {values.isFiler ? (
                      <div style={{ marginTop: "6px" }}>
                        <Label check style={{ cursor: "pointer", fontSize: "12px", color: "#000", fontStyle: "italic", fontWeight: "500", margin: 0 }}>
                          <Input
                            type="checkbox"
                            checked={values.filerUseCnic}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setValues((prev) => ({
                                ...prev,
                                filerUseCnic: checked,
                                ...(checked ? { ntnNumber: "" } : { cnic: "" })
                              }));
                              setErrors((prev) => ({ ...prev, ntnNumber: "", cnic: "" }));
                            }}
                          />{" "}
                          <GetLanguageString props="use_cnic_instead" />
                        </Label>
                      </div>
                    ) : (
                      <div style={{ marginTop: "6px", fontSize: "12px", color: "#000", fontStyle: "italic", fontWeight: "500" }}>
                        • <GetLanguageString props="transactions_vendor_cnic" />
                      </div>
                    )}
                  </FormGroup>
                </Col>

                {/* Bottom Left: Tax Status (Filer vs Non-Filer) */}
                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label" style={{ display: "flex", alignItems: "center", marginBottom: "6px" }}>
                      <span style={{ fontWeight: "600", color: "#374151", marginRight: "18px" }}>
                        <GetLanguageString props="tax_status" />:
                      </span>
                      <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                        <Label check style={{ cursor: "pointer", fontSize: "13px", margin: 0, fontWeight: "500", color: values.isFiler ? "#059669" : "#4B5563" }}>
                          <Input
                            type="radio"
                            name="isFiler"
                            value="true"
                            checked={values.isFiler === true}
                            onChange={() => {
                              setValues((prev) => ({ ...prev, isFiler: true }));
                              setErrors((prev) => ({ ...prev, ntnNumber: "", cnic: "" }));
                            }}
                          />{" "}
                          <GetLanguageString props="tax_status_filer" />
                        </Label>
                        <Label check style={{ cursor: "pointer", fontSize: "13px", margin: 0, fontWeight: "500", color: !values.isFiler ? "#D97706" : "#4B5563" }}>
                          <Input
                            type="radio"
                            name="isFiler"
                            value="false"
                            checked={values.isFiler === false}
                            onChange={() => {
                              setValues((prev) => ({ ...prev, isFiler: false, filerUseCnic: false, ntnNumber: "" }));
                              setErrors((prev) => ({ ...prev, ntnNumber: "", cnic: "" }));
                            }}
                          />{" "}
                          <GetLanguageString props="tax_status_non_filer" />
                        </Label>
                      </div>
                    </Label>
                  </FormGroup>
                </Col>

                {renderExistingAccounts()}

                <Col md="12" style={{ marginTop: "6px" }}>
                  <h5 style={sectionHeader("#059669", "#10B981")}>
                    <i className="fa fa-university"></i>
                    {isEditMode
                      ? <SectionTitle en="EDIT BANK ACCOUNT" ur="بینک اکاؤنٹ ترمیم" />
                      : <SectionTitle en="BANK ACCOUNT DETAILS" ur="بینک اکاؤنٹ کی تفصیلات" />}
                  </h5>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label"><GetLanguageString props="transactions_bank_name" /></Label>
                    <Select options={banksList} value={selectedBank} onChange={handleBankChange} placeholder={t("member_select")} classNamePrefix="select" />
                    {errors.selectedBank && <h4 className="text-error">{errors.selectedBank}</h4>}
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label"><GetLanguageString props="transactions_account_title" /></Label>
                    <Input name="accountTitle" type="text" className="no-drag" value={values.accountTitle} onChange={handleChangeText} invalid={!!errors.accountTitle} />
                    {errors.accountTitle && <h4 className="text-error no-drag">{errors.accountTitle}</h4>}
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label"><GetLanguageString props="transactions_account_number" /></Label>
                    <Input name="accountNumber" type="text" className="no-drag" value={values.accountNumber} onChange={handleChangeText} invalid={!!errors.accountNumber} />
                    {errors.accountNumber && <h4 className="text-error no-drag">{errors.accountNumber}</h4>}
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label"><GetLanguageString props="transactions_account_iban" /></Label>
                    <Input name="accountIban" type="text" className="no-drag" value={values.accountIban} onChange={handleChangeText} />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label"><GetLanguageString props="transactions_account_branch" /></Label>
                    <Input name="accountBranch" type="text" className="no-drag" value={values.accountBranch} onChange={handleChangeText} />
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>

            <ModalFooter>
              <Button color="white" className="no-drag" onClick={() => setModal(false)}>{t("common_cancel")}</Button>
              <Button color={isEditMode ? "warning" : "primary"} type="submit" className="no-drag">
                {t("common_submit")}
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Modal>
    </Draggable>
  );
};

export default AddVendorBankAccountModal;
